import { useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import { MenuCloseIcon, SunIcon, MoonIcon } from '@/components/icons/index.js';

export interface MobileDrawerGroup {
  label: string;
  items: Array<{ to: string; label: string; end?: boolean }>;
}

export interface MobileDrawerUser {
  name: string;
  isAdmin: boolean;
  onSignOut: () => void;
}

export interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  groups: MobileDrawerGroup[];
  theme: 'light' | 'dark';
  onToggleTheme: () => void;
  user?: MobileDrawerUser;
  loginHref?: string;
}

const FOCUSABLE =
  'a, button, [tabindex]:not([tabindex="-1"]), input, textarea, select, [contenteditable="true"]';

export function MobileDrawer({
  open,
  onClose,
  groups,
  theme,
  onToggleTheme,
  user,
  loginHref = '/login',
}: MobileDrawerProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    document.body.style.overflow = 'hidden';
    const t = window.setTimeout(() => {
      const first = panelRef.current?.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }, 50);
    return () => {
      window.clearTimeout(t);
      document.body.style.overflow = '';
      returnFocusRef.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
        (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
      );
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      className={`fixed inset-0 z-50 lg:hidden ${
        open ? 'pointer-events-auto' : 'pointer-events-none'
      }`}
    >
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-primary/40 motion-safe:transition-opacity motion-safe:duration-200 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className={`absolute top-0 right-0 h-full w-[80%] max-w-sm bg-parchment-deep border-l border-hairline shadow-parchment-raised flex flex-col motion-safe:transition-transform motion-safe:duration-400 motion-safe:ease-reverent ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-end p-4">
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="w-11 h-11 inline-flex items-center justify-center rounded-pill text-ink-muted hover:text-primary hover:bg-parchment-soft motion-safe:transition-colors"
          >
            <MenuCloseIcon size={22} />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-6 pb-6" aria-label="Primary">
          {groups.map((group) => (
            <div key={group.label} className="mb-7">
              <div className="font-display uppercase text-eyebrow tracking-wider text-ink-faint mb-3">
                {group.label}
              </div>
              <ul className="space-y-1">
                {group.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      onClick={onClose}
                      className={({ isActive }) =>
                        `block py-3 min-h-[44px] font-display text-lg ${
                          isActive ? 'text-gold' : 'text-primary hover:text-gold'
                        } motion-safe:transition-colors`
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>
        <div className="border-t border-hairline px-6 py-5 flex flex-col gap-3">
          <button
            type="button"
            onClick={onToggleTheme}
            className="flex items-center gap-3 py-2 min-h-[44px] text-primary hover:text-gold motion-safe:transition-colors"
          >
            {theme === 'light' ? <SunIcon size={20} /> : <MoonIcon size={20} />}
            <span className="font-display uppercase text-xs tracking-wider">
              {theme === 'light' ? 'Light' : 'Dark'}
            </span>
          </button>
          {user ? (
            <>
              <NavLink
                to="/profile"
                onClick={onClose}
                className="flex items-center justify-between py-2 min-h-[44px] text-primary hover:text-gold motion-safe:transition-colors"
              >
                <span className="font-display">{user.name}</span>
                {user.isAdmin && (
                  <span className="font-display uppercase text-[10px] tracking-wider text-gold border border-gold rounded-pill px-3 py-1">
                    Admin
                  </span>
                )}
              </NavLink>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  user.onSignOut();
                }}
                className="text-left py-2 min-h-[44px] font-display uppercase text-xs tracking-wider text-ink-muted hover:text-gold motion-safe:transition-colors"
              >
                Sign out
              </button>
            </>
          ) : (
            <NavLink
              to={loginHref}
              onClick={onClose}
              className="inline-flex items-center justify-center self-start px-5 py-2.5 rounded-pill border border-gold text-gold hover:bg-gold hover:text-bg font-display uppercase text-[11px] tracking-wider motion-safe:transition-colors"
            >
              Sign in
            </NavLink>
          )}
        </div>
      </aside>
    </div>
  );
}
